import React, { Component } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { LogoMark } from "@/components/Logo";
import Shell from "@/components/Shell";
import Login from "@/pages/Login";
import Calculateur from "@/pages/Calculateur";
import Dossiers from "@/pages/Dossiers";
import Comparateur from "@/pages/Comparateur";
import Admin from "@/pages/Admin";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[var(--bg)] p-8 text-center">
          <LogoMark size={44} />
          <h1 className="font-display text-2xl font-bold text-[var(--ink)]">Une erreur est survenue</h1>
          <p className="max-w-md text-sm text-[var(--dim)]">{String(this.state.error?.message || this.state.error)}</p>
          <button onClick={() => (window.location.href = "/")} className="btn-primary">
            Recharger le portail
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const FullPageLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-[var(--bg)]">
    <div className="float-slow">
      <LogoMark size={52} />
    </div>
  </div>
);

const ThemedToaster = () => {
  const { theme } = useTheme();
  return <Toaster position="top-right" theme={theme} richColors closeButton />;
};

const Protected = ({ children, adminOnly = false }) => {
  const { user, isAdmin } = useAuth();
  if (user === undefined) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/calculateur" replace />;
  return children;
};

const LoginRoute = () => {
  const { user } = useAuth();
  if (user === undefined) return <FullPageLoader />;
  if (user) return <Navigate to="/calculateur" replace />;
  return <Login />;
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginRoute />} />
              <Route
                element={
                  <Protected>
                    <Shell />
                  </Protected>
                }
              >
                <Route path="/" element={<Navigate to="/calculateur" replace />} />
                <Route path="/calculateur" element={<Calculateur />} />
                <Route path="/dossiers" element={<Dossiers />} />
                <Route path="/comparateur" element={<Comparateur />} />
                <Route
                  path="/admin"
                  element={
                    <Protected adminOnly>
                      <Admin />
                    </Protected>
                  }
                />
              </Route>
              <Route path="*" element={<Navigate to="/calculateur" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
        <ThemedToaster />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
